import {
  organizationLimitQueryOptions,
  organizationLimitSaveMutation,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type {
  LimitTypeBeanWritable,
  OrgLimitBean,
} from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { defineEntitySchema } from './base/EntitySchema';

// Limits are only read and saved as part of an organization, so this schema has no get endpoint.
export const organizationLimitSchema = defineEntitySchema({
  name: 'organizationLimit',
  label: 'Organization Limit',
  endpoints: {
    // Each result holds one organization's limits; every limit is an entity.
    query: organizationLimitQueryOptions,
    toItems: (settings: OrgLimitBean): LimitTypeBeanWritable[] => settings.limits ?? [],
    // The API saves an organization's limits as one list.
    edit: {
      mutation: organizationLimitSaveMutation,
      toRequest: (organizationId, _body, row) => ({
        path: { id: organizationId },
        body: { limits: row.items },
      }),
      newItem: () => ({ type: 'COMP_SET_MAX_SIZE' as const, value: 0 }),
    },
    // Deleting saves the list again, with the limit flagged as deleted.
    delete: {
      mutation: organizationLimitSaveMutation,
      toRequest: (organizationId, row) => ({
        path: { id: organizationId },
        body: {
          limits: [
            {
              ...row.items[row.index],
              deleted: true,
            },
          ],
        },
      }),
    },
  },
  primaryText: 'type',
  fields: [
    {
      key: 'type',
      label: 'Limit',
      type: 'enum',
      options: ['COMP_SET_MAX_SIZE', 'MAX_USERS'],
      convertItemValue: (value) => {
        if (value === 'COMP_SET_MAX_SIZE') return { type: 'COMP_SET_MAX_SIZE' as const };
        if (value === 'MAX_USERS') return { type: 'MAX_USERS' as const };
        throw new Error('Choose a limit type.');
      },
    },
    {
      key: 'value',
      label: 'Value',
      type: 'number',
      integer: true,
      convertItemValue: (value) => {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
          throw new Error('Value must be a number.');
        }
        return { value };
      },
    },
  ],
});
